// src/pages/journal/ReflectionPage.tsx - AI-powered reflection page

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { 
  fetchJournalEntry,
  fetchReflection,
  createReflection
} from '../../store/slices/journalSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

// Mock AI reflection generation (in a real app, this would call OpenAI API)
const generateReflection = async (content: string, depth: number): Promise<string> => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Simple mock responses based on depth
  if (depth === 1) {
    return `I notice you've been reflecting on your experiences. It takes courage to look inward and document your thoughts. Consider how these moments connect to your broader journey.`;
  } else if (depth === 2) {
    return `Looking at your entry, I'm noticing patterns around how you process challenges. There seems to be a theme of resilience, but also some recurring thought patterns that might be worth examining further. How might these patterns connect to your core values?`;
  } else {
    return `This entry reveals significant insights about your internal framework. I notice a tension between your stated values and some of your reactions. This cognitive dissonance often indicates an opportunity for growth. Consider how your identity is shaped by these experiences, and whether there are deeper beliefs that need examination. What would it look like to fully align your actions with your highest principles?`;
  }
};

const ReflectionPage: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { currentEntry, currentReflection, isLoading, error } = useSelector((state: RootState) => state.journal);
  const { settings } = useSelector((state: RootState) => state.settings);
  
  const [reflectionDepth, setReflectionDepth] = useState(2);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!entryId) {
      navigate('/journal');
      return;
    }
    
    if (currentUser) {
      dispatch(fetchJournalEntry(entryId));
      
      // If entry has reflection, fetch it
      if (currentEntry?.reflectionGenerated && currentEntry?.reflectionId) {
        dispatch(fetchReflection(currentEntry.reflectionId));
      }
    }
  }, [entryId, currentUser, dispatch]);
  
  useEffect(() => {
    // Set default reflection depth from settings if available
    if (settings?.defaultReflectionDepth) {
      setReflectionDepth(settings.defaultReflectionDepth);
    }
  }, [settings]);
  
  const handleGenerateReflection = async () => {
    if (!currentUser || !currentEntry || !entryId) return;
    
    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      // In a real app, this would call the OpenAI API
      const reflectionContent = await generateReflection(currentEntry.content, reflectionDepth);
      
      await dispatch(createReflection({
        userId: currentUser.uid,
        entryId: entryId,
        content: reflectionContent,
        depthLevel: reflectionDepth
      }));
    } catch (err: any) {
      setGenerationError(err.message || 'Failed to generate reflection');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleDepthChange = (event: React.MouseEvent<HTMLElement>, newDepth: number | null) => {
    if (newDepth !== null) {
      setReflectionDepth(newDepth);
    }
  };
  
  const handleBack = () => {
    navigate('/journal');
  };
  
  const getDepthDescription = (depth: number) => {
    switch (depth) {
      case 1:
        return "Surface observations and gentle encouragement";
      case 2:
        return "Moderate analysis with pattern recognition";
      case 3:
        return "Deep insights and challenging questions";
      default:
        return "";
    }
  };
  
  if (isLoading && !currentEntry) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          Clarity Reflection
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {generationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {generationError}
          </Alert>
        )}
        
        {currentEntry && (
          <Paper elevation={3} sx={{ p: 4, width: '100%', mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Your Journal Entry
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {new Date(currentEntry.timestamp).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {currentEntry.content}
            </Typography>
          </Paper>
        )}
        
        {!currentReflection ? (
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Generate Reflection
            </Typography>
            
            <Typography variant="body1" paragraph>
              The Clarity Reflection assistant uses AI to provide insights based on your journal entry. Choose the depth level for your reflection:
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <ToggleButtonGroup
                value={reflectionDepth}
                exclusive
                onChange={handleDepthChange}
                aria-label="reflection depth"
                sx={{ mb: 2 }}
              >
                <ToggleButton value={1} aria-label="level 1">
                  Level 1
                </ToggleButton>
                <ToggleButton value={2} aria-label="level 2">
                  Level 2
                </ToggleButton>
                <ToggleButton value={3} aria-label="level 3">
                  Level 3
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Typography variant="body2" color="text.secondary">
                {getDepthDescription(reflectionDepth)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isGenerating}
              >
                Back to Journal
              </Button>
              
              <Button
                variant="contained"
                onClick={handleGenerateReflection}
                disabled={isGenerating || !currentEntry}
              >
                {isGenerating ? <CircularProgress size={24} /> : 'Generate Reflection'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Clarity Reflection
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Depth Level: {currentReflection.depthLevel} • Generated on {new Date(currentReflection.timestamp).toLocaleDateString()}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                {currentReflection.content}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                >
                  Back to Journal
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default ReflectionPage;
